import { SogniClient } from '@sogni-ai/sogni-client';
import { useEffect, useState } from 'react';

async function initializeClient() {
  console.log('🚀 Starting client initialization (cookies auth)');

  let appId = localStorage.getItem('sogni-appId');
  if (!appId) {
    appId = window.crypto.randomUUID();
    localStorage.setItem('sogni-appId', appId);
    console.log('🆔 Generated new appId');
  }

  // Match sogni-photobooth environment detection
  const hostname = window.location.hostname;
  const isLocalDev = hostname === 'localhost' || hostname === '127.0.0.1';
  const isStaging = hostname.includes('staging');
  const restEndpoint = isLocalDev
    ? 'https://api-local.sogni.ai'
    : isStaging
    ? 'https://api-staging.sogni.ai'
    : 'https://api.sogni.ai';
  const socketEndpoint = isLocalDev
    ? 'wss://socket-local.sogni.ai'
    : isStaging
    ? 'wss://socket-staging.sogni.ai'
    : 'wss://socket.sogni.ai';

  const client = await SogniClient.createInstance({
    appId,
    restEndpoint,
    socketEndpoint,
    jsonRpcUrl: process.env.REACT_APP_RPC_URL,
    network: 'fast',
    logLevel: 'debug',
    authType: 'cookies',
    testnet: isLocalDev || isStaging
  });

  // Listen for auth state changes (matching sogni-web pattern)
  client.apiClient.auth.on('updated', (isAuthenticated) => {
    console.log('🔑 Auth state changed:', isAuthenticated);
  });

  // Persist only username for UX (avoid storing tokens)
  client.account.currentAccount.on('updated', () => {
    const acct = client.account.currentAccount;
    console.log('👤 currentAccount updated:', {
      isAuthenicated: acct.isAuthenicated,
      username: acct.username,
      walletAddress: acct.walletAddress
    });
    if (acct.username) {
      sessionStorage.setItem('sogni-username', acct.username);
    }
  });

  // Try to resume existing cookie-based session
  try {
    const isAuthenticated = await client.checkAuth();
    console.log('🔐 checkAuth result:', isAuthenticated);
    const acct = client.account.currentAccount;
    console.log('🔐 Account after checkAuth:', {
      isAuthenicated: acct.isAuthenicated,
      username: acct.username,
      walletAddress: acct.walletAddress
    });
  } catch (e) {
    console.warn('No existing authenticated session', e);
  }

  return client;
}

/**
 * Get the SogniClient instance
 * @returns {SogniClient | null}
 */
export function useSogniClient() {
  const [client, setClient] = useState(null);

  useEffect(() => {
    console.log('🎬 useSogniClient hook starting');
    let mounted = true;

    const init = async () => {
      try {
        console.log('📦 Checking session storage', {
          username: sessionStorage.getItem('sogni-username')
        });

        const newClient = await initializeClient();
        console.log('✨ Client initialized');

        if (mounted) {
          console.log('🔄 Setting client in state');
          setClient(newClient);
        }
      } catch (err) {
        console.error('❌ Error initializing client:', err);
      }
    };

    init();

    return () => {
      console.log('🧹 Cleaning up useSogniClient');
      mounted = false;
    };
  }, []); // Empty deps array to run once on mount

  return client;
}

function getAccountData(account) {
  // Gate on isAuthenicated (matching sogni-web pattern), not username
  // which may not be populated yet when the auth state changes
  if (!account?.isAuthenicated) {
    return null;
  }
  return {
    username: account.username,
    walletAddress: account.walletAddress,
    balance: account.balance
  };
}

/**
 * Get the current account data, and update it when the account changes
 * @param {SogniClient} client - SogniClient instance
 * @returns {{username: string, walletAddress: string, balance: number} | null}
 */
export function useCurrentAccount(client) {
  const account = client?.account.currentAccount;
  const [data, setData] = useState(getAccountData(account));

  useEffect(() => {
    if (!account) {
      setData(null);
      return;
    }

    const handleUpdate = () => {
      const updatedData = getAccountData(account);
      console.log('🔄 useCurrentAccount handleUpdate:', updatedData);
      setData(updatedData);
      if (updatedData?.username) {
        sessionStorage.setItem('sogni-username', updatedData.username);
      }
    };

    // Immediate check (account may already be authenticated from checkAuth)
    handleUpdate();

    // Listen for account entity changes
    const unsubAccount = account.on('updated', () => {
      handleUpdate();
    });

    // Also listen for auth state changes as belt-and-suspenders
    let unsubAuth;
    if (client?.apiClient?.auth) {
      unsubAuth = client.apiClient.auth.on('updated', () => {
        console.log('🔑 useCurrentAccount: auth event, re-checking account');
        handleUpdate();
      });
    }

    return () => {
      if (typeof unsubAccount === 'function') unsubAccount();
      if (typeof unsubAuth === 'function') unsubAuth();
    };
  }, [account, client]);

  return data;
}
